import {
  ArrowsDownUpIcon,
  CaretDownIcon,
  MagnifyingGlassIcon,
  SlidersHorizontalIcon,
} from "@phosphor-icons/react";
import clsx from "clsx";
import { times } from "lodash-es";
import { useIntl } from "react-intl";

import {
  FilterButton,
  FilterButtonSkeleton,
} from "@/components/FilterButton/FilterButton";
import { HeaderSearchButton } from "@/components/HeaderSearchButton/HeaderSearchButton";
import { StickyHeader } from "@/components/StickyHeader/StickyHeader";
import { HotelsListDrawerComponent } from "@/features/hotels/components/hotels-list/types";
import { useHotelSearchDataContext } from "@/features/hotels/contexts/HotelSearchDataContext";
import {
  HotelsSortOptions,
  type HotelsSortValue,
  type IHotelsFilters,
} from "@/features/hotels/types/types";
import { getTotalVisitors } from "@/features/hotels/utils/rooms";
import { formatHeaderDate } from "@/utils/dates";

type Props = {
  onFilterButtonClick: (drawerComponent: HotelsListDrawerComponent) => void;
  selectedSortValue: HotelsSortValue;
  progress?: number;
  onSearchDrawerOpen: () => void;
  activeFilters:
    | (Partial<Record<keyof IHotelsFilters, boolean>> & {
        searchByName: boolean;
      })
    | null;
};

export function HotelListHeader({
  onFilterButtonClick,
  selectedSortValue,
  progress,
  activeFilters,
  onSearchDrawerOpen,
}: Props) {
  const { searchData } = useHotelSearchDataContext();
  const intl = useIntl();

  const sortOptionsLabels = {
    [HotelsSortOptions.PRICE]: intl.formatMessage({
      defaultMessage: "Cheapest",
      description: "Sort option: cheapest hotels first",
    }),
    [HotelsSortOptions.RATING]: intl.formatMessage({
      defaultMessage: "Hotel Rating",
      description:
        "Sort option: highest-rated hotels first (by star rating and price)",
    }),
    [HotelsSortOptions.RECOMMENDED]: intl.formatMessage({
      defaultMessage: "Most Popular",
      description: "Sort option: recommended / most popular hotels first",
    }),
    [HotelsSortOptions.SPECIALDEAL]: intl.formatMessage({
      defaultMessage: "Exclusive offers",
      description: "Sort option: hotels with special deals first",
    }),
  };

  return (
    <StickyHeader>
      <div className="flex items-center justify-between gap-2 py-2 text-text-primary">
        <div className="grow overflow-hidden">
          <HeaderSearchButton
            onClick={() => {
              onSearchDrawerOpen();
            }}
            progress={
              progress != null
                ? { variant: "determinate", value: progress }
                : undefined
            }
          >
            <h3 className="w-full truncate text-start font-medium text-text-title">
              {searchData?.destination?.name}
            </h3>

            <div className="w-full truncate text-xs font-normal text-text-tertiary">
              {[
                `${formatHeaderDate(searchData!.dates.checkIn)} - ${formatHeaderDate(searchData!.dates.checkOut)}`,
                intl.formatMessage(
                  {
                    defaultMessage:
                      "{count, plural, one {# guest} other {# guests}}",
                    description: "Guest count summary label",
                  },
                  { count: getTotalVisitors(searchData!.rooms) },
                ),
                intl.formatMessage(
                  {
                    defaultMessage:
                      "{count, plural, one {# room} other {# rooms}}",
                    description: "Room count summary label",
                  },
                  { count: searchData!.rooms.length },
                ),
              ].map((item, index) => (
                <span key={index}>
                  {index > 0 ? <> &bull; </> : null}
                  {item}
                </span>
              ))}
            </div>
          </HeaderSearchButton>
        </div>
      </div>

      <div
        className={clsx(
          "mb-2 flex gap-1 pt-px pb-2",
          progress !== undefined ? "overflow-x-hidden" : "overflow-x-auto",
        )}
      >
        {progress !== undefined ? (
          times(5, (idx) => <FilterButtonSkeleton key={idx} />)
        ) : (
          <>
            <FilterButton
              onClick={() =>
                onFilterButtonClick(HotelsListDrawerComponent.SEARCH_BY_NAME)
              }
              active={activeFilters?.searchByName || false}
            >
              {intl.formatMessage({
                defaultMessage: "Search by name",
                description:
                  "Label for the hotel name search input field in filters",
              })}
              <MagnifyingGlassIcon size={20} className="fill-text-primary" />
            </FilterButton>
            <FilterButton
              onClick={() =>
                onFilterButtonClick(HotelsListDrawerComponent.SORT)
              }
            >
              {sortOptionsLabels[selectedSortValue.field]}
              <ArrowsDownUpIcon size={20} className="fill-text-primary" />
            </FilterButton>
            <FilterButton
              onClick={() =>
                onFilterButtonClick(HotelsListDrawerComponent.ALL_FILTERS)
              }
              active={Object.values(activeFilters || {}).some(Boolean)}
            >
              {intl.formatMessage({
                defaultMessage: "All filters",
                description: "Button to open the full hotel filters panel",
              })}
              <SlidersHorizontalIcon size={20} className="fill-text-primary" />
            </FilterButton>
            <FilterButton
              onClick={() => {
                onFilterButtonClick(HotelsListDrawerComponent.HOTEL_CLASS);
              }}
              active={activeFilters?.starRating || false}
            >
              {intl.formatMessage({
                defaultMessage: "Star rating",
                description:
                  "Filter section label for filtering by hotel star class",
              })}
              <CaretDownIcon size={16} />
            </FilterButton>
            <FilterButton
              onClick={() =>
                onFilterButtonClick(HotelsListDrawerComponent.PRICE)
              }
              active={activeFilters?.priceRange ?? false}
            >
              {intl.formatMessage({
                defaultMessage: "Price",
                description:
                  "Filter chip label to open the standalone price drawer",
              })}
              <CaretDownIcon size={16} />
            </FilterButton>
          </>
        )}
      </div>
    </StickyHeader>
  );
}
